/**
 * Get Client Key Helper
 *
 * Retrieves the client_key for a user from their global user document.
 * Supports caching to avoid repeated reads.
 *
 * Usage:
 * const result = await getClientKey(userId, cachedClientKey);
 * if (result.success) {
 *   return result.clientKey; // 'athleto'
 * }
 */

import { getDoc } from 'firebase/firestore';
import { GlobalUserRef_Document } from '../refs/globalUsers';

export interface GetClientKeyResult {
  success: boolean;
  clientKey: string;
  error?: {
    message: string;
    details: string;
    code: string;
  };
}

/**
 * Gets the client_key for a user
 *
 * @param userId - The user's UID
 * @param cachedClientKey - Optional cached client_key to avoid Firestore read
 * @returns Promise with success status and client_key
 */
export const getClientKey = async (
  userId: string,
  cachedClientKey?: string | null
): Promise<GetClientKeyResult> => {
  // Return cached value if available
  if (cachedClientKey != null && cachedClientKey !== '') {
    return {
      success: true,
      clientKey: cachedClientKey,
    };
  }

  try {
    // Fetch global user document
    const globalUserRef = GlobalUserRef_Document(userId);
    const globalUserSnap = await getDoc(globalUserRef);

    if (!globalUserSnap.exists()) {
      return {
        success: false,
        clientKey: '',
        error: {
          message: 'Global user document not found',
          details: `No global user document exists for user ${userId}`,
          code: 'ER-DB-GCK-01',
        },
      };
    }

    const globalUserData = globalUserSnap.data();
    const clientKey = globalUserData?.client_key;

    if (!clientKey || clientKey === '') {
      return {
        success: false,
        clientKey: '',
        error: {
          message: 'Client key not set',
          details: 'Global user document exists but client_key field is empty or missing',
          code: 'ER-DB-GCK-02',
        },
      };
    }

    return {
      success: true,
      clientKey,
    };

  } catch (error: any) {
    return {
      success: false,
      clientKey: '',
      error: {
        message: 'Failed to fetch client key',
        details: error.message || 'Unknown error occurred',
        code: 'ER-DB-GCK-03',
      },
    };
  }
};

/**
 * Gets client key with callback to set context
 * This version matches the pattern from your other project
 *
 * @param userId - The user's UID
 * @param cachedClientKey - Optional cached client_key
 * @param callbackToSetContext - Optional callback to cache the result
 * @returns Promise with success status and client_key
 */
export const getClientKeyWithCallback = async (
  userId: string,
  cachedClientKey?: string | null,
  callbackToSetContext?: (value: string) => void
): Promise<GetClientKeyResult> => {
  const result = await getClientKey(userId, cachedClientKey);

  // If successful and callback provided, cache the result
  if (result.success && callbackToSetContext && typeof callbackToSetContext === 'function') {
    callbackToSetContext(result.clientKey);
  }

  return result;
};
